/**
 * Owner 2026-10-22 14:40: "jak mam kliknięte nowe wydarzenie i obok kalendarz, to chcę, zaznaczając w kalendarzu,
 * zmieniać datę albo zakres dat". While "Nowe wydarzenie" / "Nowe wypożyczenie" is open in the side panel, the days
 * picked in the calendar (a click, a drag, Shift = a run, Cmd / Ctrl = single days) are the form's dates, and the
 * form's dates are what the calendar marks. Pure mappings both ways (the same rule as `dni=`: one run of days is the
 * first and last day, runs with gaps are terms).
 */
import { isDay } from '../planning/wizard';
import type { DaySelection } from './calendar';
import { addDays, runsOf } from './labels';

/** Longest run marked (as `nextSelection`): a typo year does not make the calendar walk a thousand years. */
const MAX_RUN = 400;

const sortedUnique = (days: readonly string[]) => [...new Set(days)].sort();
const sameDays = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((d, i) => d === b[i]);

/** The days from `first` to `last`, both included; only `first` when the last day is missing or earlier; [] without a first day. */
export function daysBetween(first: string, last: string): string[] {
  if (!isDay(first)) return [];
  if (!isDay(last) || last < first) return [first];
  const run: string[] = [];
  for (let d = first; d <= last && run.length < MAX_RUN; d = addDays(d, 1)) run.push(d);
  return run;
}

/** The dates as the wizard (and "Nowe wypożyczenie") keep them: `days` only when the picked days have gaps. */
export type PickedDates = { firstDay: string; lastDay: string; days: string[] };

/** Picked days → the form's dates. Nothing picked → no dates (the form then says what is missing). */
export function datesOfPick(picked: readonly string[]): PickedDates {
  const days = sortedUnique(picked.filter(isDay));
  const runs = runsOf(days);
  if (runs.length === 0) return { firstDay: '', lastDay: '', days: [] };
  return { firstDay: runs[0]!.first, lastDay: runs.at(-1)!.last, days: runs.length > 1 ? days : [] };
}

/** The form's dates → the days the calendar marks. */
export function pickOfDates(d: PickedDates): string[] {
  return d.days.length > 1 ? sortedUnique(d.days.filter(isDay)) : daysBetween(d.firstDay, d.lastDay);
}

type Term = { firstDay: string; lastDay: string; firstTime: string; lastTime: string };

/**
 * The plain "Nowe wydarzenie" form (a role that may add events but not plan): the first run of days is its day
 * fields, the other runs its further terms. Hours already typed stay with the term at the same place.
 */
export function termsOfPick(picked: readonly string[], cur: { firstTime: string; lastTime: string; moreTerms?: readonly Term[] | undefined }): Term & { moreTerms: Term[] } {
  const [first, ...rest] = runsOf(sortedUnique(picked.filter(isDay)));
  return {
    firstDay: first?.first ?? '',
    lastDay: first?.last ?? '',
    firstTime: cur.firstTime,
    lastTime: cur.lastTime,
    moreTerms: rest.map((r, i) => ({ firstDay: r.first, lastDay: r.last, firstTime: cur.moreTerms?.[i]?.firstTime ?? '', lastTime: cur.moreTerms?.[i]?.lastTime ?? '' })),
  };
}

/** Every day of the plain form's terms, for the calendar. */
export function pickOfTerms(d: { firstDay: string; lastDay: string; moreTerms?: readonly Term[] | undefined }): string[] {
  return sortedUnique([d, ...(d.moreTerms ?? [])].flatMap((t) => daysBetween(t.firstDay, t.lastDay)));
}

/**
 * The calendar's selection after the form's dates changed: unchanged when the days are the same (so Shift still runs
 * from its anchor), else those days, with the anchor kept when it is still among them.
 */
export function syncSelection(cur: DaySelection, days: readonly string[]): DaySelection {
  const next = sortedUnique(days);
  if (sameDays(cur.days, next)) return cur;
  if (next.length === 0) return { days: [], anchor: null, base: [] };
  return { days: next, anchor: cur.anchor && next.includes(cur.anchor) ? cur.anchor : next[0]!, base: [] };
}

/** A drag from `from` over to `to`: that run (with Cmd / Ctrl added to the days picked before); Shift later runs from `from`. */
export function dragSelection(cur: DaySelection, from: string, to: string, add: boolean): DaySelection {
  const run = from <= to ? daysBetween(from, to) : daysBetween(to, from);
  const base = add ? cur.days.filter((d) => !run.includes(d)) : [];
  return { days: sortedUnique([...base, ...run]), anchor: from, base };
}
